import { Component, OnInit, OnDestroy, Renderer2, inject, DestroyRef } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CompanyService, StorageService, UserService, AppService, AuthService, SocketService, RoleService } from './core/services';
import { Company } from './shared/models/company.model';
import { User } from './shared/models/user.model';
import { environment } from 'src/environments/environment';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Router, RouterModule } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
/**
 * App Component
 */

@Component({
  selector: 'mccc-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [RouterModule]
})
/**
 * App Component
 * @class AppComponent
 */
export class AppComponent implements OnInit, OnDestroy {
  /**
   * inject dependencies
   * @param translateService
   */
  private translateService = inject(TranslateService);
  /**
   * inject dependencies
   * @param companyService
   */
  private companyService = inject(CompanyService);
  /**
   * inject dependencies
   * @param authService
   */
  private authService = inject(AuthService);
  /**
   * inject dependencies
   * @param appService
   */
  private appService = inject(AppService);
  /**
   * inject dependencies
   * @param socketService
   */
  private socketService = inject(SocketService);
  /**
   * inject dependencies
   * @param storageService
   */
  private storageService = inject(StorageService);
  /**
   * inject dependencies
   * @param roleService
   */
  private roleService = inject(RoleService);
  /**
   * inject dependencies
   * @param modalService
   */
  private modalService = inject(NgbModal);
  /**
   * inject dependencies
   * @param userService
   */
  private userService = inject(UserService);
  /**
   * timeout property
   * @type {any}
   * @default null
   */
  private timeout: any = null;
  /**
   * loading property
   * @type {boolean}
   * @default true
   */
  public loading: boolean = true;
  private renderer = inject(Renderer2);
  private destroyRef = inject(DestroyRef);

  router = inject(Router);


  /**
   * OnInit lifecycle hook
   * @description
   * 1) load user preferences from local storage
   * 2) set the theme based on the user preferences, or default to light theme
   * 3) set the translations based on user preferences, browser language or default language
   * 4) when the company and user are loaded, start the socket service for the company and user
   * 5) if the user has the right permissions, start the progress notification
   * 6) if the user has a token, get the current company and user and start the socket service
   * 7) if the user does not have a token, set loading to false
   */
  ngOnInit(): void {
    this.appService.SubCurrentActiveTheme()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((theme) => {
        if (theme === 'dark') {
          this.renderer.removeClass(document.body, 'light-mode');
          this.renderer.addClass(document.body, 'dark-mode');
        } else {
          this.renderer.removeClass(document.body, 'dark-mode');
          this.renderer.addClass(document.body, 'light-mode');
        }
      });
    this.storageService.loadUserPref();
    const localStorageTheme = this.storageService.getTheme();
    if (localStorageTheme && ['light', 'dark'].includes(localStorageTheme)) {
      this.appService.setCurrentActiveTheme(localStorageTheme);
    } else {
      this.appService.setCurrentActiveTheme('light');
    }
    this.translateService.addLangs(['en', 'es', 'fr', 'pt', 'zh-tw']);
    //, 'zh-tw', 'de', 'it'
    this.translateService.setDefaultLang(environment.defaultLang);
    const localStorageLang = this.storageService.getLang();
    const browserLang = this.translateService.getBrowserLang();
    if (localStorageLang && ['pt', 'fr', 'en', 'es', 'zh-tw'].includes(localStorageLang)) {
      //, 'zh-tw', 'de', 'it'

      this.translateService.use(localStorageLang);
    } else if (browserLang && ['pt', 'fr', 'en', 'es', 'zh-tw'].includes(browserLang)) {
      //, 'zh-tw', 'de', 'it'

      this.translateService.use(browserLang);
      this.storageService.setLang(browserLang);
    } else {
      this.translateService.use(environment.defaultLang);
      this.storageService.setLang(environment.defaultLang);
    }
    this.userService.SubCurrentUser()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((user) => {
        //, 'zh-tw', 'de', 'it'

        if (user && user.lang && ['pt', 'fr', 'en', 'es', 'zh-tw'].includes(user.lang)) {
          this.translateService.use(user.lang);
          this.storageService.setLang(user.lang);
        }
      });
    this.companyService.SubCurrentCompany()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((company) => {
        clearTimeout(this.timeout);
        this.timeout = null;
        if (company && company.endsAt) {
          const disabledAfter = new Date(company.endsAt.replace(/-/g, '/')).getTime() - new Date().getTime();
          if (disabledAfter > 0) {
            this.timeout = this.myTimeOut(() => {
              this.modalService.dismissAll();
              this.authService.logout(true, false, true);
            }, disabledAfter);
          } else {
            this.authService.logout(true, false, true);
          }
        }
      });
    // case when we have a token and the application refresh for some reason do we need to get our current user and company
    if (this.storageService.getToken()) {
      this.#loadCurrentCompanyAndUser();
    } else {
      this.loading = false;
    }
  }

  /**
   * This function is a wrapper over setTimeout
   * @function myTimeOut
   * @description This function is a wrapper over setTimeout but allows to set
   * a timeout greater than MAX_INT32 (2^31-1). If the time difference is
   * greater than MAX_INT32 the function will call itself with the remaining
   * difference.
   * @param {function} func - The function to be called when the timeout ends
   * @param {number} diff - The time difference in milliseconds
   * @returns {number} The timeout ID
   */
  myTimeOut(func, diff) {
    if (diff > 0x7FFFFFFF) { // setTimeout limit is MAX_INT32=(2^31-1)
      return setTimeout(() => { this.myTimeOut(func, diff); }, 0x7FFFFFFF);
    } else {
      return setTimeout(func, diff);
    }
  }
  ngOnDestroy(): void {
    if (this.timeout) {
      clearTimeout(this.timeout);
      this.timeout = null;
    }
  }

  #loadCurrentCompanyAndUser(): void {
    Promise.all([this.companyService.getCurrentCompany(),
    this.userService.getCurrentUser()])
      .then(([response1, response2]) => {
        if (response1.success === 1 && response2.success === 1) {
          const company = new Company(response1.Company);
          const user = new User(response2.user);
          this.companyService.setCurrentCompany(company);
          this.userService.setCurrentUser(user);
          this.socketService.refreshSocket(company.id, user.id);
          this.authService.startSocket();
          this.companyService.startSocket();
          this.userService.startSocket();
          this.roleService.startSocket();
          if (user.role.permission.manageApplicationAccess || user.role.permission.manageCreditCards || user.role.permission.managePreloadPasswords || user.role.permission.managePreloadUrls || user.role.permission.manageUsers) {
            this.userService.startProgressNotification();
          }
        } else {
          this.authService.logout(true);
        }
      })
      .catch((err) => {
        if (err.status !== 401 && err.status !== 403 && err.status !== 0) {
          this.authService.logout(true);
        }
      }).finally(() => {
        this.loading = false;
      });
  }
}
